import { SUPABASE_URL } from './utils/supabase/url';
import { useBillingRecoveryToken } from './platform/auth/billingRecoverySession';
import { Suspense, useState, useEffect, useCallback, useMemo, useRef, useTransition } from 'react';
import { getAppShellConfig } from './layout-system';
import { useAuth } from './platform/auth/AuthContext';
import { DataService } from './services/DataService';
import { NativeBridge } from './services/NativeBridge';
import {
  suppressCanvasErrors,
  forwardConsoleSafely,
  isBenignPdfNoise,
  isBenignPdfNoiseArgs,
  isExtensionNoise,
  isExtensionNoiseArgs,
} from './utils/suppressCanvasErrors';
import './utils/debugCanvasErrors'; // Auto-loads if DEBUG_CANVAS is set
import { toast } from 'sonner';
import { useLocalStorageState } from './hooks/useLocalStorageState';
import { useContextualSearch } from './hooks/useContextualSearch';
import { useLogos } from './hooks/useLogos';
import { initKeyboardHandler } from './utils/keyboardHandler';
import { useBrowserPermissionsBootstrap } from './hooks/useBrowserPermissionsBootstrap';
import { AppProviders } from './app/AppProviders';
import { AppShellProviders } from './app/AppShellProviders';
import { AuthenticatedController } from './app/AuthenticatedController';
import { AuthenticatedWorkspace } from './app/AuthenticatedWorkspace';
import { NativeSidePanelApp } from './app/NativeSidePanelApp';
import { NativeAddProjectApp } from './app/NativeAddProjectApp';
import { ViewLoader } from './app/ViewLoader';
import { isUnknownRoute, isLegacyReportingPath } from './utils/routeTable';
import { RouteNotFound } from './platform/RouteNotFound';
import {
  buildWorkspacePathById,
  buildProjectPathById,
  buildTaskPathById,
} from './utils/prettyRouteBuilders';
import { projectId } from './utils/supabase/info';
import {
  parsePrettyWorkspacePath,
  workspaceDetailTabFromSection,
  parsePrettyProjectDocumentPath,
  parsePrettyProjectTeamMemberPath,
  parsePrettyProjectTabPath,
  parsePrettySharedPath,
  parsePrettyTaskPath,
  slugifySegment,
} from './utils/prettyRoutes';
import {
  buildNativeHeaderState,
  buildNativeNavItems,
  WORKSPACES_TAB_PREFIX,
  getAppViewTitle,
  getNativeRouteItemIdentifier,
  isNativeContextualSearchRoute,
  isAppView,
  NATIVE_TAB_TO_VIEW,
  PROJECT_TAB_PREFIX,
  SYSTEM_SETTINGS_TAB_PREFIX,
  type AppView,
  VIEW_TO_NATIVE_TAB,
} from './app/navigation';
import { lazyChunk } from './utils/lazyChunk';
import { parsePersonalAccountPath } from './utils/personalAccountRoutes';
import { buildViewPath, matchViewPath } from './utils/subRoutes';
import { openSettingsModal, usesSettingsModal } from './features/preferences/settingsModalStore';
import { parseOutreachSlug } from './features/outreach/constants';
import { readProjectCalendarScope } from './utils/projectCalendarRoute';

import type { AddProjectTab } from './features/projects/add-project-form/types';
import { markReturnToCompanies } from './features/contacts/companies/companyRoutes';
import { EMPLOYERS_STATE_EVENT } from './features/employers/constants';
import { supabase } from './utils/supabase/client';
import { workspaceLockKey } from './platform/auth/workspaceLock';
import { prefetchProjectPermissions } from './features/projects/hooks/useProjectPermissions';
const BillingRecoveryPage = lazyChunk(() => import('./features/billing/BillingRecoveryPage'));
const LoginPage = lazyChunk(() => import('./platform/auth/LoginPage'), (m) => m.LoginPage);
const WebLaunchScreen = lazyChunk(() => import('./platform/auth/WebLaunchScreen'));
const GmailAddonConnectionPage = lazyChunk(() => import('./features/settings/GmailAddonSettings'), (m) => m.GmailAddonSettings);
const RegisterPage = lazyChunk(() => import('./platform/auth/RegisterPage'), (m) => m.RegisterPage);
const ClientSignup = lazyChunk(() => import('./platform/auth/ClientSignup'), (m) => m.ClientSignup);
const MemberInvite = lazyChunk(() => import('./platform/auth/MemberInvite'), (m) => m.MemberInvite);
const StartSoftwarePage = lazyChunk(() => import('./platform/auth/StartSoftwarePage'), (m) => m.StartSoftwarePage);
const XeroCallback = lazyChunk(() => import('./platform/auth/XeroCallback'), (m) => m.XeroCallback);
const PublicDocumentViewer = lazyChunk(() => import('./platform/sharing/PublicDocumentViewer'), (m) => m.PublicDocumentViewer);
const WalletVerificationPage = lazyChunk(() => import('./features/billing/WalletVerificationPage'), (m) => m.WalletVerificationPage);
const PublicBookingPage = lazyChunk(() => import('./features/calendar/PublicBookingPage'), (m) => m.PublicBookingPage);
const DayworkSignPage = lazyChunk(() => import('./features/projects/dayworks/DayworkSignPage'), (m) => m.DayworkSignPage);
const WorkflowRunPage = lazyChunk(() => import('./features/workflows/run/WorkflowRunPage'), (m) => m.WorkflowRunPage);
const McpConnectionPage = lazyChunk(() => import('./features/settings/McpConnectionPage'), (m) => m.McpConnectionPage);

function GoogleWorkspaceCallbackBridge({ code, state, error }: { code: string | null; state: string | null; error: string | null; }) {
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!code || !state || error) {
        if (!cancelled) window.location.replace('/');
        return;
      }

      try {
        // Retrieve the current session token; fall back to the anon key for
        // unauthenticated OAuth callbacks so the edge function can verify the
        // `state` parameter server-side without a 401/500.
        const { data: sessionData } = await supabase.auth.getSession();
        const token =
          sessionData?.session?.access_token ??
          (import.meta.env.VITE_SUPABASE_ANON_KEY as string);

        const response = await fetch(
          `${SUPABASE_URL}/functions/v1/make-server-a476273b/google-workspace/callback`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ code, state }),
          }
        );
        if (!response.ok) {
          throw new Error('callback failed');
        }
      } catch {
        // silent
      } finally {
        if (!cancelled) window.location.replace('/');
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [code, state, error]);

  return null;
}

/** The path to treat as the entry address; a retired /reports-insights link is rewritten to Home first. */
function readEntryPathname(): string {
  const { pathname } = window.location;
  if (!isLegacyReportingPath(pathname)) return pathname;
  window.history.replaceState(window.history.state, '', '/');
  return '/';
}

function AppContent() {
  const billingRecoveryToken = useBillingRecoveryToken();
  const auth = useAuth();
  const [workspaceSessionLocked, setWorkspaceSessionLocked] = useState(false);
  useEffect(() => {
    setWorkspaceSessionLocked(false);
    if (!auth.user?.id) return;
    let cancelled = false;
    let checking = false;
    const check = async () => {
      if (checking || document.visibilityState === 'hidden') return;
      checking = true;
      try {
        const { data, error } = await supabase.rpc('workspace_session_blocked');
        if (!cancelled && !error && data === true) {
          sessionStorage.setItem(workspaceLockKey, 'true');
          setWorkspaceSessionLocked(true);
        }
      } catch (error) {
        console.warn('Unable to check workspace access', error);
      } finally { checking = false; }
    };
    void check();
    const timer = window.setInterval(check, 15000);
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, [auth.user?.id]);
  const { logoFullLight, logoFullDark } = useLogos();
  const nativePermissionBootstrapKey = 'quantis-native-permissions-bootstrap-v1';

  useEffect(() => {
    // Initialize mobile keyboard handler
    const cleanup = initKeyboardHandler();
    return cleanup;
  }, []);

  useEffect(() => {
    if (auth.status !== 'authenticated' || !auth.user || !NativeBridge.isRunningNatively()) return;

    try {
      if (window.localStorage.getItem(nativePermissionBootstrapKey) === '1') {
        return;
      }

      window.localStorage.setItem(nativePermissionBootstrapKey, '1');
      NativeBridge.requestPermissions();
    } catch (error) {
      console.error('[App] Failed to bootstrap native permissions', error);
    }
  }, [auth.status, auth.user, nativePermissionBootstrapKey]);

  // Web browser permissions (notifications + persistent storage).
  // Only runs for authenticated web users — native shell has its own bootstrap above.
  // The hook now triggers the native OS notification prompt directly on the
  // user's first interaction post-login. No branded pre-prompt.
  const userId = auth.status === 'authenticated' ? (auth.user?.id ?? null) : null;
  useBrowserPermissionsBootstrap(userId);
  
  // -- View and selection state --
  // The path the user actually arrived on. Held in state rather than read from
  // window at render time because the boot sequence rewrites the URL while it
  // restores the previous session's view — reading live would replace a dead
  // link with a valid path mid-render. Refreshed only on popstate, so the
  // browser Back button clears the not-found screen while an internal
  // replaceState does not.
  const [entryPathname, setEntryPathname] = useState(
    () => (typeof window !== 'undefined' ? readEntryPathname() : '/'),
  );
  useEffect(() => {
    const onPop = () => setEntryPathname(readEntryPathname());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const [currentView, setCurrentView] = useLocalStorageState<AppView>(
    'currentView',
    'dashboard',
    {
      deserialize: (value) => value as AppView,
      // In the browser Preferences is a modal, never a page to come back to.
      validate: (value) => isAppView(value) && !(value === 'preferences' && usesSettingsModal())
    }
  );
  
  const [calendarProjectScope, setCalendarProjectScope] = useState<string | null>(
    () => readProjectCalendarScope(window.location.pathname, window.location.search),
  );

  // Wrap setCurrentView in startTransition to avoid suspension errors during navigation.
  // useTransition gives us isPending so we can show immediate feedback in the content area.
  const [isNavigating, startNavigation] = useTransition();
  const navigateToView = useCallback((view: AppView) => {
    startNavigation(() => {
      if (view !== 'calendar') setCalendarProjectScope(null);
      setCurrentView(view);
    });
  }, [setCurrentView]);
  
  const [nativeShellHidesWebMainHeader, setNativeShellHidesWebMainHeader] = useState(false);
  const shouldPassWebHeader = !nativeShellHidesWebMainHeader;
  const lastAuthenticatedEntryUserIdRef = useRef<string | null>(null);

  // Which project is open is NOT persisted — the address is the only source of
  // truth for it, restored by AuthenticatedController's applyLocationState (with
  // a DataService retry for slugs that need loaded data). Persisting it gave the
  // previous session's project a head start over the URL on boot, so a deep link
  // could resolve to one project's name over the last project's id. Anything
  // keyed on projectId — Documents most visibly, which fetches a Drive folder
  // root per project — then loaded against the wrong project and blanked.
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [selectedProjectName, setSelectedProjectName] = useState<string | null>(null);

  // Drop the keys previous versions wrote, so a returning session cannot be
  // seeded from them by any code path that still reads localStorage directly.
  useEffect(() => {
    localStorage.removeItem('selectedProjectId');
    localStorage.removeItem('selectedProjectName');
  }, []);

  const [selectedClientId, setSelectedClientId] = useLocalStorageState<string | null>('selectedClientId', null);
  const [selectedClientName, setSelectedClientName] = useLocalStorageState<string | null>('selectedClientName', null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [taskRouteSlug, setTaskRouteSlug] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [pendingChatId, setPendingChatId] = useState<string | null>(null);
  // Two SEPARATE slots, not one reused both ways — that reuse was tried and
  // caused a feedback loop: MessagesView consuming a boot-parsed target wrote
  // back through the same setter the announce effect used for the live URL,
  // so every resolution re-triggered the announce and vice versa, pushing
  // the URL back and forth forever.
  // `messagesChatSlug` — MessagesView announces here on every open/close;
  // drives the /messages/[chatSlug] URL only.
  const [messagesChatSlug, setMessagesChatSlug] = useState<string | null>(null);
  // `pendingChatSlug` — write-once boot target: set only when a /messages/
  // [chatSlug] URL is parsed, consumed and cleared once MessagesView resolves
  // it against its loaded chat list, same shape as pendingChatId above.
  const [pendingChatSlug, setPendingChatSlug] = useState<string | null>(null);
  const [documentRouteSlug, setDocumentRouteSlug] = useState<string | null>(null);
  // Events tab sub-route (open detail / form), e.g. "ev-001", "ev-001/instruction",
  // "new-daywork", "unlinked". Mirrors documentRouteSlug for deep-linking.
  // NOTE: this is the second hand-threaded per-tab sub-route slug. If a THIRD
  // tab needs one, replace these parallel slugs with a single per-tab map
  // (tab -> sub-route) + a {parse,build} registry rather than threading a third.
  const [eventsRouteSlug, setEventsRouteSlug] = useState<string | null>(null);
  const projectSelectionInFlightRef = useRef(false);
  const getCompany = useCallback((id?: string | null) => DataService.getCompany(id), []);
  const handleNotesCountChange = useCallback((count: number) => {
    setProjectTabProps(prev => prev ? { ...prev, notesCount: count } : prev);
  }, []);
  
  // -- UI State --
  const [signupToken, setSignupToken] = useState<string | null>(null);
  // Distinct param name from signupToken's `token` — /signup?token= (new
  // workspace off a Website checkout) and /join?invite= (joining an existing
  // workspace) are different flows with different token domains and must
  // never be confused for one another.
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [shareToken, setShareToken] = useState<string | null>(null);
  const [showRegister, setShowRegister] = useState(false);
  const [projectListProps, setProjectListProps] = useState<any>(null);
  // Persisted so a browser refresh on a project tab (BOQ, documents, etc.) keeps
  // the user on that tab even before DataService can resolve the URL slugs.
  const [projectActiveTabRaw, setProjectActiveTab] = useLocalStorageState<string>(
    'projectActiveTab',
    'overview'
  );
  const projectActiveTab = projectActiveTabRaw ?? 'overview';
  // Employers only searches its directory, and this is the FIRST operand of
  // the OR that decides whether the native search bar shows
  // (AuthenticatedController's searchVisible) — gating only the controller's
  // own call would achieve nothing, because this one would still say yes.
  const [employersListVisible, setEmployersListVisible] = useState(false);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ listVisible?: boolean } | null>).detail;
      setEmployersListVisible(!!detail?.listVisible);
    };
    window.addEventListener(EMPLOYERS_STATE_EVENT, handler);
    return () => window.removeEventListener(EMPLOYERS_STATE_EVENT, handler);
  }, []);
  const supportsNativeContextualSearch = useMemo(
    () => isNativeContextualSearchRoute({ currentView: currentView!, projectActiveTab, employersListVisible }),
    [currentView, projectActiveTab, employersListVisible]
  );
  const { isAvailable: showContextualSearch, activate: activateContextualSearch } =
    useContextualSearch(supportsNativeContextualSearch);
  const [projectPermissions, setProjectPermissions] = useState<any>(null);
  // The Parties step was split into Contractor + Employer; the
  // initial controlled tab now points at the new first step
  // (`contractor`). The previous default was the retired `'client'`
  // value, which is no longer in `AddProjectTab` — leaving it set to
  // `'client'` left the form's right pane blank because none of the
  // active-tab conditional renders matched.
  const [addProjectActiveTab, setAddProjectActiveTab] = useState<AddProjectTab>('contractor');
  const [addProjectCompletedTabs, setAddProjectCompletedTabs] = useState<Set<AddProjectTab>>(new Set());
  const [clientRequestActiveTab, setClientRequestActiveTab] = useState<string>('company');
  const [clientRequestCompletedTabs, setClientRequestCompletedTabs] = useState<Set<string>>(new Set());
  const [userManagementActiveTab, setUserManagementActiveTab] = useState<string>('users');
  const [clientDetailActiveTab, setClientDetailActiveTab] = useState<string>('overview');
  const [workspacesActiveTab, setWorkspacesActiveTab] = useState<'clients' | 'requests' | 'add-client'>('clients');
  const [workspacesStats, setWorkspacesStats] = useState({ total: 0, active: 0, invited: 0, pending: 0, pendingRequests: 0 });
  const [mainContractorInitialTab, setMainContractorInitialTab] = useState<'contractors' | 'add-contractor'>('contractors');
  const [systemSettingsActiveTab, setSystemSettingsActiveTab] = useState<string>('integrations');
  // null is the phone's section list. Seeded from the address so a cold deep
  // link does not flash the list before the controller's boot parser runs.
  const [preferencesSection, setPreferencesSection] = useState<string | null>(() => {
    const route = matchViewPath(window.location.pathname);
    return route?.view === 'preferences' ? route.slug : null;
  });
  const shellConfig = useMemo(() => {
    if (currentView === 'employers' && mainContractorInitialTab === 'add-contractor') {
      return getAppShellConfig('employers-add');
    }
    const config = getAppShellConfig(currentView!);
    if (currentView === 'system-settings' && systemSettingsActiveTab.startsWith('templates')) {
      return { ...config, scrollMode: 'locked' as const };
    }
    return config;
  }, [currentView, mainContractorInitialTab, systemSettingsActiveTab]);
  const [profileActiveTab, setProfileActiveTab] = useState<string>(() => {
    // Route guards run before the controller's URL-restoration effect. Seed
    // the section now so they cannot rewrite a workspace deep link to Account.
    const pathname = window.location.pathname;
    const personal = parsePersonalAccountPath(pathname);
    if (personal) return personal.section || 'overview';
    const route = matchViewPath(pathname);
    return route && (route.view === 'profile' || route.view === 'account')
      ? route.slug || 'overview'
      : 'overview';
  });
  // Shared with the controller and lazy Outreach view so Pipeline intent
  // survives the period before the destination chunk has mounted.
  const [outreachActiveTab, setOutreachActiveTab] = useState<string | null>(() => {
    const route = matchViewPath(window.location.pathname);
    // Section only: the open record is the controller's own outreachRecordId.
    return route?.view === 'outreach' ? parseOutreachSlug(route.slug)?.section ?? null : null;
  });
  const [emailActiveTab, setEmailActiveTab] = useState<string>('INBOX');
  const [newlyCreatedUser, setNewlyCreatedUser] = useState<any | null>(null);
  const [projectFilterStage, setProjectFilterStage] = useState<string | undefined>(undefined);
  const [interimInitialTab, setInterimInitialTab] = useState<string | undefined>(undefined);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [messagePanelOpen, setMessagePanelOpen] = useState(false);
  // Dedicated to "the Calendar's Tasks sub-view is open" — deliberately NOT
  // reset by opening notifications/messages, which used to alias this via a
  // shared taskQueueOpen flag: closing Notifications from the header pill
  // would silently rewrite the URL off /calendar/tasks as a side effect,
  // since the flag doubled as this route's own state. Booking still resets
  // it (see toggleBookingPanel) because both render in the same shell slot
  // gated on the same currentView==='calendar' check and would otherwise
  // fight over which one the ternary in AuthenticatedWorkspace shows.
  const [calendarTasksTabOpen, setCalendarTasksTabOpen] = useState(false);
  const setCalendarTasksTab = useCallback((open: boolean) => {
    if (open) setCalendarProjectScope(null);
    setCalendarTasksTabOpen(open);
  }, []);
  const [bookingPanelOpen, setBookingPanelOpen] = useState(false);
  const [panelChatId, setPanelChatId] = useState<string | null>(null);
  const [panelThreadMessageId, setPanelThreadMessageId] = useState<string | null>(null);
  const pageTitle = useMemo(() => {
    // Look up the project number from DataService so it appears in the header
    // title across every project tab without patching each tab individually.
    // DataService.getProject is synchronous; selectedProjectId in the deps
    // ensures the memo re-runs when the user switches projects.
    const selectedProjectNumber =
      currentView === 'project-detail' && selectedProjectId
        ? (DataService.getProject(selectedProjectId)?.projectNumber ?? null)
        : null;
    return getAppViewTitle({
      currentView: currentView!,
      selectedProjectName,
      selectedProjectNumber,
      selectedClientName,
      systemSettingsActiveTab,
      preferencesSection,
      profileActiveTab,
      emailActiveTab,
    });
  }, [currentView, selectedProjectId, selectedProjectName, selectedClientName, systemSettingsActiveTab, preferencesSection, profileActiveTab, emailActiveTab]);
  const isSupervisorShellRoute =
    (auth.user?.role === 'workspace_user' || auth.user?.role === 'supervisor') &&
    (
      currentView === 'dashboard' ||
      currentView === 'progress' ||
      currentView === 'daily-diary' ||
      currentView === 'deliveries'
    );

  // -- Computed project tab props for sidebar navigation --
  const [projectTabProps, setProjectTabProps] = useState<{
    isClient: boolean;
    isEstimatingPhase: boolean;
    activeTasksCount: number;
    isStaff: boolean;
    projectId?: string;
    projectName?: string;
    projectStage?: string;
    internalUserIds?: string[];
    notesCount?: number;
    runsTender?: boolean;
    issuesContractDocuments?: boolean;
    applicationWorkflow?: import('./contract-administration/types').ApplicationWorkflow;
    canGivePayLessNotice?: boolean;
    isSiteSupervisor?: boolean;
  } | null>(null);

  // -- Handlers --
  const handleShowDashboard = useCallback(() => {
    startNavigation(() => {
      setCurrentView('dashboard');
      setSelectedProjectId(null);
      setSelectedProjectName(null);
      setSelectedClientId(null);
      setSelectedClientName(null);
      setSelectedTaskId(null);
      setSelectedUserId(null);
      setPendingChatId(null);
      setDocumentRouteSlug(null);
      setEventsRouteSlug(null);
      setTaskRouteSlug(null);
      setProjectActiveTab('overview');
    });
  }, [
    startNavigation,
    setCurrentView,
    setSelectedClientId,
    setSelectedClientName,
    setSelectedProjectId,
    setSelectedProjectName,
    setSelectedTaskId,
  ]);

  useEffect(() => {
    const authenticatedUserId = auth.user?.id ?? null;

    if (!authenticatedUserId) {
      lastAuthenticatedEntryUserIdRef.current = null;
      return;
    }

    if (lastAuthenticatedEntryUserIdRef.current === authenticatedUserId) {
      return;
    }

    lastAuthenticatedEntryUserIdRef.current = authenticatedUserId;

    // Native shells can remount or rehydrate during navigation, and forcing a
    // dashboard reset here causes those shells to snap back home on every
    // authenticated re-entry. Keep the existing view when running natively.
    if (!NativeBridge.isRunningNatively()) {
      // If the URL already points at an app route (e.g. after a page refresh),
      // let AuthenticatedController's applyLocationState() restore it instead
      // of blowing it away with a dashboard redirect. Only redirect when coming
      // from a bare login page (pathname is '/', '/login', '/register', etc.).
      const { pathname } = window.location;
      // OAuth callbacks (Google Workspace, etc.) land back on `/?code=…&state=…`
      // in a popup that's already auth'd from the parent. If we treat that as a
      // bare landing page we replaceState `/` and strip the OAuth params before
      // Google Workspace callback's lazy chunk has loaded its effect to read them,
      // and the popup ends up in the misleading "idle / All done" state with
      // no token exchange. Bail out on anything carrying OAuth callback params.
      const oauthCallbackParams = new URLSearchParams(window.location.search);
      // Treat any OAuth callback-ish URL as in-flight so we don't eagerly
      // replaceState('/') before callback handlers can read query params.
      // Some providers/cases can omit `state` or return only `error`.
      const isOAuthCallback =
        oauthCallbackParams.has('code') ||
        oauthCallbackParams.has('state') ||
        oauthCallbackParams.has('error');
      const isAuthPage =
        pathname === '/' ||
        pathname.startsWith('/login') ||
        pathname.startsWith('/register') ||
        pathname.startsWith('/signup') ||
        // Otherwise a signed-in admin clicking a test invite link bounces
        // straight to the dashboard instead of seeing the acceptance page.
        pathname.startsWith('/join');

      if (isAuthPage && !isOAuthCallback) {
        handleShowDashboard();
        window.history.replaceState({ view: 'dashboard' }, document.title, '/');
      }
    }
  }, [auth.user?.id, handleShowDashboard]);

  const handleShowProjects = useCallback((stageFilter?: string) => {
    startNavigation(() => {
      setCurrentView('projects');
      setSelectedProjectId(null);
      setSelectedProjectName(null);
      setDocumentRouteSlug(null);
      setEventsRouteSlug(null);
      setTaskRouteSlug(null);
      setProjectFilterStage(stageFilter);
    });
  }, [startNavigation, setCurrentView, setSelectedProjectId, setSelectedProjectName]);

  const handleSelectProject = useCallback((projectId: string, projectName: string) => {
    if (!projectId || projectId === 'activity' || projectId === 'undefined' || projectId === 'null') {
      console.error('🚫 App: Blocked invalid projectId from being set:', projectId);
      toast.error('Invalid project selected');
      return;
    }
    if (currentView === 'project-detail' && selectedProjectId === projectId) return;
    prefetchProjectPermissions(projectId, userId);
    projectSelectionInFlightRef.current = true;
    startNavigation(() => {
      setCurrentView('project-detail');
      setSelectedProjectId(projectId);
      setSelectedProjectName(projectName);
      setDocumentRouteSlug(null);
      setEventsRouteSlug(null);
      setProjectActiveTab('overview');
    });
  }, [currentView, selectedProjectId, startNavigation, setCurrentView, setSelectedProjectId, setSelectedProjectName, userId]);

  const handleSelectProjectTab = useCallback((projectId: string, projectName: string, tab: string) => {
    if (!projectId || projectId === 'activity' || projectId === 'undefined' || projectId === 'null') {
      console.error('🚫 App: Blocked invalid projectId from being set:', projectId);
      toast.error('Invalid project selected');
      return;
    }
    prefetchProjectPermissions(projectId, userId);
    projectSelectionInFlightRef.current = true;
    startNavigation(() => {
      setCurrentView('project-detail');
      setSelectedProjectId(projectId);
      setSelectedProjectName(projectName);
      setDocumentRouteSlug(null);
      setEventsRouteSlug(null);
      setTaskRouteSlug(null);
      setProjectActiveTab(tab || 'overview');
    });
  }, [startNavigation, setCurrentView, setSelectedProjectId, setSelectedProjectName, userId]);

  const handleSelectClient = useCallback((clientId: string, clientName: string, section?: string) => {
    const detailTab = workspaceDetailTabFromSection(section) ?? 'overview';
    // Opens the Workspaces split (directory beside the detail, section nav
    // in the app sidebar) - NOT the legacy full-page client-detail view,
    // whose self-contained nav panel duplicated the sidebar. Organisation is
    // the default page, so it owns the short /workspaces/{slug} address.
    const detailView = window.matchMedia('(min-width: 1024px)').matches ? 'workspaces' : 'client-detail';
    const url = buildWorkspacePathById(clientId, { activeTab: detailTab });
    window.history.pushState({ view: detailView, clientId, clientName }, '', url);
    startNavigation(() => {
      setCurrentView(detailView);
      setSelectedClientId(clientId);
      setSelectedClientName(clientName);
      setDocumentRouteSlug(null);
      setEventsRouteSlug(null);
      setTaskRouteSlug(null);
      setClientDetailActiveTab(detailTab);
    });
  }, [startNavigation, setCurrentView, setSelectedClientId, setSelectedClientName]);

  const handleShowCalendarWithTask = useCallback((taskId?: string) => {
    startNavigation(() => {
      setCalendarProjectScope(null);
      setCurrentView('calendar');
      setCalendarTasksTabOpen(true);
      setSelectedTaskId(taskId || null);
      setTaskRouteSlug(null);
      setDocumentRouteSlug(null);
      setEventsRouteSlug(null);
    });
  }, [startNavigation, setCurrentView]);

  const handleShowUserManagement = useCallback(() => {
    startNavigation(() => { setCurrentView('user-management'); });
  }, [startNavigation, setCurrentView]);

  const handleShowWorkspaces = useCallback(() => {
    startNavigation(() => {
      setCurrentView('contacts');
      setSelectedClientId(null);
      setSelectedClientName(null);
    });
  }, [startNavigation, setCurrentView, setSelectedClientId, setSelectedClientName]);

  const handleShowEmployers = useCallback((initialTab?: 'contractors' | 'add-contractor') => {
    startNavigation(() => {
      setCurrentView(initialTab === 'add-contractor' ? 'employers' : 'contacts');
      setMainContractorInitialTab(initialTab || 'contractors');
    });
  }, [startNavigation, setCurrentView]);

  const handleShowProfile = useCallback(() => {
    startNavigation(() => { setCurrentView('profile'); });
  }, [startNavigation, setCurrentView]);

  const handleShowNotifications = useCallback(() => {
    startNavigation(() => {
      setCurrentView('notifications');
      setNotificationOpen(false);
      setMessagePanelOpen(false);
    });
  }, [startNavigation, setCurrentView]);

  const handleShowSupport = useCallback(() => {
    startNavigation(() => {
      setCurrentView('support');
      setNotificationOpen(false);
      setMessagePanelOpen(false);
    });
  }, [startNavigation, setCurrentView]);

  const handleShowMessages = useCallback(() => {
    startNavigation(() => {
      setCurrentView('messages');
      setNotificationOpen(false);
      setMessagePanelOpen(false);
    });
  }, [startNavigation, setCurrentView]);

  const handleShowSystemSettings = useCallback(() => {
    startNavigation(() => { setCurrentView('system-settings'); });
  }, [startNavigation, setCurrentView]);

  const handleLogout = useCallback(() => {
    auth.logout();
    startNavigation(() => {
      setCurrentView('dashboard');
      setSelectedProjectId(null);
      setSelectedProjectName(null);
      setDocumentRouteSlug(null);
      setEventsRouteSlug(null);
      setTaskRouteSlug(null);
    });
  }, [auth, startNavigation, setCurrentView, setSelectedProjectId, setSelectedProjectName]);

  const handleShowAddUserForm = useCallback(() => {
    startNavigation(() => { setCurrentView('user-form'); });
  }, [startNavigation, setCurrentView]);

  const handleCloseClientDetail = useCallback(() => {
    markReturnToCompanies();
    startNavigation(() => {
      setCurrentView('contacts');
      setSelectedClientId(null);
      setSelectedClientName(null);
      setDocumentRouteSlug(null);
      setEventsRouteSlug(null);
      setTaskRouteSlug(null);
    });
  }, [startNavigation, setCurrentView, setSelectedClientId, setSelectedClientName]);

  const handleOpenChatThread = useCallback((chatId: string, messageId?: string | null) => {
    startNavigation(() => {
      setCurrentView('messages');
      setPendingChatId(chatId);
      setPanelThreadMessageId(messageId ?? null);
      setNotificationOpen(false);
      setMessagePanelOpen(false);
    });
  }, [startNavigation, setCurrentView]);

  const openMessagesPanel = useCallback((chatId?: string | null, messageId?: string | null) => {
    setNotificationOpen(false);
    setMessagePanelOpen(true);
    setPanelChatId(chatId ?? null);
    setPanelThreadMessageId(messageId ?? null);
  }, []);

  const toggleNotificationsPanel = useCallback(() => {
    setNotificationOpen(prev => !prev);
    setMessagePanelOpen(false);
    // Deliberately NOT touching calendarTasksTabOpen — Notifications is
    // checked ahead of it in AuthenticatedWorkspace's rightPanel ternary, so
    // it already wins the shared slot without needing the Tasks route
    // cleared out from under the user. See calendarTasksTabOpen's own
    // declaration for the bug this used to cause.
    setBookingPanelOpen(false);
    setPanelThreadMessageId(null);
  }, []);

  const toggleMessagesPanel = useCallback((chatId?: string, messageId?: string | null) => {
    // The desktop header can inspect Messages alongside the current page.
    // Keep the phone route's full-screen conversation from opening a second list.
    if (currentView === 'messages' && !chatId && !messagePanelOpen && !window.matchMedia('(min-width: 1024px)').matches) {
      return;
    }
    setMessagePanelOpen(prev => !prev);
    setNotificationOpen(false);
    // See toggleNotificationsPanel — same reasoning, Messages also wins the
    // shared slot ahead of Tasks without clearing calendarTasksTabOpen.
    setBookingPanelOpen(false);
    setPanelChatId(typeof chatId === 'string' ? chatId : null);
    setPanelThreadMessageId(messageId ?? null);
  }, [currentView, messagePanelOpen]);

  const toggleTaskQueuePanel = useCallback(() => {
    setCalendarProjectScope(null);
    setCalendarTasksTabOpen(prev => !prev);
    setNotificationOpen(false);
    setMessagePanelOpen(false);
    setBookingPanelOpen(false);
  }, []);

  const toggleBookingPanel = useCallback(() => {
    setBookingPanelOpen(prev => !prev);
    setNotificationOpen(false);
    setMessagePanelOpen(false);
    // Booking and Tasks share both the same shell slot AND the same
    // currentView==='calendar' gate, immediately adjacent in the rightPanel
    // ternary — unlike Notifications/Messages above, ternary order alone
    // can't disambiguate them, so this one still has to clear the other.
    setCalendarTasksTabOpen(false);
  }, []);

  const closeRightPanels = useCallback(() => {
    setNotificationOpen(false);
    setMessagePanelOpen(false);
    setCalendarTasksTabOpen(false);
    setBookingPanelOpen(false);
    setPanelChatId(null);
    setPanelThreadMessageId(null);
  }, []);

  const handleCloseProjectDetail = useCallback(() => {
    startNavigation(() => {
      setCurrentView('projects');
      setSelectedProjectId(null);
      setSelectedProjectName(null);
      setDocumentRouteSlug(null);
      setEventsRouteSlug(null);
    });
  }, [startNavigation, setCurrentView, setSelectedProjectId, setSelectedProjectName]);

  const handleShowInterimValuations = useCallback((tab?: string) => {
    startNavigation(() => {
      setCurrentView('interim-valuations');
      setInterimInitialTab(tab);
    });
  }, [startNavigation, setCurrentView]);

  const handleShowAdminPanel = useCallback(() => {
    startNavigation(() => { setCurrentView('admin'); });
  }, [startNavigation, setCurrentView]);

  // Platform billing lives in the Sales hub now (Subscriptions / Platform
  // Revenue), so the admin panel's tile opens Sales rather than the standalone
  // screen it was named for. The finance-billing view is retained only so old
  // /billing links keep resolving.
  const handleShowFinanceBilling = useCallback(() => {
    startNavigation(() => { setCurrentView('sales'); });
  }, [startNavigation, setCurrentView]);

  const handleWorkspaceNavigate = useCallback((view: AppView) => {
    switch (view) {
      case 'dashboard':
        handleShowDashboard();
        break;
      case 'projects':
        handleShowProjects();
        break;
      case 'tasks':
        handleShowCalendarWithTask();
        break;
      case 'workspaces':
        handleShowWorkspaces();
        break;
      case 'user-management':
        handleShowUserManagement();
        break;
      case 'employers':
        handleShowEmployers();
        break;
      case 'profile':
        handleShowProfile();
        break;
      case 'notifications':
        handleShowNotifications();
        break;
      case 'support':
        handleShowSupport();
        break;
      case 'messages':
        handleShowMessages();
        break;
      case 'system-settings':
        handleShowSystemSettings();
        break;
      default:
        navigateToView(view);
        break;
    }
  }, [
    handleShowWorkspaces,
    handleShowDashboard,
    handleShowEmployers,
    handleShowMessages,
    handleShowNotifications,
    handleShowSupport,
    handleShowProfile,
    handleShowProjects,
    handleShowSystemSettings,
    handleShowCalendarWithTask,
    handleShowUserManagement,
    navigateToView,
  ]);

  // -- Derived Effects --
  const isPopState = useRef(false);
  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);

  useEffect(() => {
    suppressCanvasErrors();
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
    document.documentElement.classList.toggle('standalone', isStandalone);
  }, []);

  useEffect(() => {
    const originalWarn = console.warn;
    const originalError = console.error;
    console.warn = (...args: any[]) => {
      if (isExtensionNoiseArgs(args)) return;
      if (isBenignPdfNoiseArgs(args)) return;
      const message = args[0]?.toString() || '';
      if (message.includes('Could not access stylesheet') || message.includes('cssRules') || message.includes('toDataURL') || message.includes('Tainted canvases') || message.includes('SecurityError')) return;
      forwardConsoleSafely(originalWarn, args);
    };
    console.error = (...args: any[]) => {
      if (isExtensionNoiseArgs(args)) return;
      if (isBenignPdfNoiseArgs(args)) return;
      const message = args[0]?.toString() || '';
      if (message.includes('Could not access stylesheet') || message.includes('cssRules') || message.includes('toDataURL') || message.includes('Tainted canvases') || message.includes('SecurityError') || isBenignPdfNoise(message)) return;
      forwardConsoleSafely(originalError, args);
    };
    const handleError = (event: ErrorEvent) => {
      const message = event.message || '';
      if (isExtensionNoise(message) || isExtensionNoise(event.filename)) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
      if (message.includes('toDataURL') || message.includes('Tainted canvases') || message.includes('HTMLCanvasElement')) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };
    window.addEventListener('error', handleError);
    return () => {
      console.warn = originalWarn;
      console.error = originalError;
      window.removeEventListener('error', handleError);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !('gesturestart' in window)) return;
    const preventDefault = (e: Event) => e.preventDefault();
    document.addEventListener('gesturestart', preventDefault, { passive: false });
    document.addEventListener('gesturechange', preventDefault, { passive: false });
    document.addEventListener('gestureend', preventDefault, { passive: false });
    return () => {
      document.removeEventListener('gesturestart', preventDefault);
      document.removeEventListener('gesturechange', preventDefault);
      document.removeEventListener('gestureend', preventDefault);
    };
  }, []);

  useEffect(() => {
    let meta = document.querySelector('meta[name="viewport"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'viewport');
      document.head.appendChild(meta);
    }
    const content = meta.getAttribute('content') || '';
    if (!content.includes('viewport-fit=cover')) {
      meta.setAttribute('content', content ? `${content}, viewport-fit=cover` : 'width=device-width, initial-scale=1, viewport-fit=cover');
    }
  }, []);

  useEffect(() => {
    const handleGlobalNav = (e: any) => {
      const {
        view,
        projectId,
        projectName,
        projectTab,
        taskId,
        chatId,
        messageId,
        clientId,
        clientName,
        settingsSection,
      } = e.detail || {};
      if (view === 'project-detail' && projectId) {
        handleSelectProject(projectId, projectName || 'Project');
        if (typeof projectTab === 'string' && projectTab.length > 0) {
          setTimeout(() => {
            setProjectActiveTab(projectTab);
          }, 0);
        }
      } else if (view === 'tasks' || (view === 'calendar' && typeof taskId === 'string' && taskId.length > 0)) {
        // Legacy `view=tasks` events and new `view=calendar&taskId=...` events both
        // route to the calendar view. The taskId (if present) causes the Tasks tab to open.
        handleShowCalendarWithTask(typeof taskId === 'string' && taskId.length > 0 ? taskId : undefined);
      } else if (view === 'messages') {
        if (typeof chatId === 'string' && chatId.length > 0) {
          openMessagesPanel(chatId, typeof messageId === 'string' ? messageId : null);
        } else {
          handleShowMessages();
        }
      } else if (view === 'client-detail' && clientId) {
        handleSelectClient(clientId, clientName || 'Client');
      } else if (view === 'preferences' && usesSettingsModal()) {
        openSettingsModal(typeof settingsSection === 'string' ? settingsSection : null);
      } else if (view === 'preferences') {
        // No section means "open Preferences fresh": the phone list, or the
        // first section on desktop. Keeping the last section would land a
        // phone on a pane with no list behind it.
        setPreferencesSection(typeof settingsSection === 'string' && settingsSection.length > 0 ? settingsSection : null);
        navigateToView(view);
      } else if (view && isAppView(view)) {
        navigateToView(view);
        if (typeof settingsSection === 'string' && settingsSection.length > 0) {
          setSystemSettingsActiveTab(settingsSection);
        }
      }
    };
    // The Tasks panel reports the task it has open; the URL writer reads
    // selectedTaskId, so this is what moves the address to
    // /calendar/tasks/[task] and back. Same value is a no-op in React.
    const handleTaskOpenChange = (e: Event) => {
      const taskId = (e as CustomEvent<{ taskId?: string | null }>).detail?.taskId ?? null;
      setSelectedTaskId(taskId);
      setTaskRouteSlug(null);
    };
    window.addEventListener('app:navigate' as any, handleGlobalNav);
    window.addEventListener('tasks:open-change', handleTaskOpenChange);
    return () => {
      window.removeEventListener('app:navigate' as any, handleGlobalNav);
      window.removeEventListener('tasks:open-change', handleTaskOpenChange);
    };
  }, [
    handleSelectClient,
    handleSelectProject,
    handleShowMessages,
    handleShowCalendarWithTask,
    navigateToView,
    openMessagesPanel,
    setProjectActiveTab,
  ]);

  // macOS native projects list → web navigation.
  // Fires when the user taps a row in MacProjectsList (Swift). The native shell
  // dispatches quantis:native:openProject with { projectId } via WKWebView
  // evaluateJavaScript. This listener is always mounted (top-level App) so it
  // works regardless of which screen is currently shown in the webview.
  // Guarded to native shells only to avoid stray events in browser dev mode.
  useEffect(() => {
    const handleOpenProject = (e: Event) => {
      const detail = (e as CustomEvent<{ projectId?: string }>).detail;
      const projectId = detail?.projectId;
      if (typeof projectId !== 'string' || !projectId) return;
      // Resolve the project name from the in-memory cache so the detail
      // header shows the right title immediately; falls back to 'Project'
      // if the cache hasn't synced yet (the detail page will re-fetch).
      const name = DataService.getProject(projectId)?.name ?? 'Project';
      handleSelectProject(projectId, name);
    };
    window.addEventListener('quantis:native:openProject', handleOpenProject);
    return () => window.removeEventListener('quantis:native:openProject', handleOpenProject);
  }, [handleSelectProject]);

  useEffect(() => {
    const token = urlParams.get('token');
    if (token) setSignupToken(token);
    const invite = urlParams.get('invite');
    if (invite) setInviteToken(invite);
    const share = urlParams.get('share');
    if (share) setShareToken(share);
    const sharedDocumentRoute = parsePrettySharedPath(window.location.pathname);
    if (sharedDocumentRoute) setShareToken(sharedDocumentRoute.token);
  }, [urlParams]);

  useEffect(() => {
    const isPlainRootPath = window.location.pathname === '/';
    const isProjectsListPath = matchViewPath(window.location.pathname)?.view === 'projects';
    const hasExplicitRouteIntent =
      urlParams.has('token') ||
      urlParams.has('share') ||
      urlParams.has('code') ||
      urlParams.has('state') ||
      urlParams.has('boqProjectId');

    if ((!isPlainRootPath && !isProjectsListPath) || hasExplicitRouteIntent) return;

    const hasPersistedProjectContext =
      currentView === 'project-detail' ||
      selectedProjectId !== null ||
      selectedProjectName !== null ||
      documentRouteSlug !== null;

    if (!hasPersistedProjectContext) return;
    if (projectSelectionInFlightRef.current) return;

    const targetView = isProjectsListPath ? 'projects' : 'dashboard';
    startNavigation(() => {
      setCurrentView(targetView);
      setSelectedProjectId(null);
      setSelectedProjectName(null);
      setDocumentRouteSlug(null);
      setEventsRouteSlug(null);
      setTaskRouteSlug(null);
    });
    window.history.replaceState(
      { view: targetView },
      document.title,
      isProjectsListPath ? `${buildViewPath('projects')}${window.location.search}` : '/'
    );
  }, [
    currentView,
    documentRouteSlug,
    startNavigation,
    setCurrentView,
    selectedProjectId,
    selectedProjectName,
    setDocumentRouteSlug,
    setSelectedProjectId,
    setSelectedProjectName,
    setTaskRouteSlug,
    urlParams,
  ]);

  useEffect(() => {
    if (currentView === 'project-detail' && selectedProjectId) {
      projectSelectionInFlightRef.current = false;
      return;
    }

    if (projectSelectionInFlightRef.current) {
      return;
    }

    if (currentView === 'project-detail') return;
    if (
      selectedProjectId === null &&
      selectedProjectName === null &&
      documentRouteSlug === null
    ) {
      return;
    }

    const isNativeShell =
      typeof window !== 'undefined' && (window as any).__QUANTIS_NATIVE__ === true;
    const pathname = window.location.pathname;
    const isProjectRoute =
      Boolean(parsePrettyProjectDocumentPath(pathname)) ||
      Boolean(parsePrettyProjectTeamMemberPath(pathname)) ||
      Boolean(parsePrettyProjectTabPath(pathname)) ||
      /^\/projects\/[^/]+(?:\/[^/]+)?\/?$/.test(pathname);

    // Native route reporting can briefly wobble the app view away from
    // `project-detail` while the URL is still a project route. Clearing the
    // project context here forces Documents to remount and re-bootstrap.
    if (isNativeShell && isProjectRoute) {
      return;
    }

    setSelectedProjectId(null);
    setSelectedProjectName(null);
    setDocumentRouteSlug(null);
    setEventsRouteSlug(null);
  }, [
    currentView,
    documentRouteSlug,
    selectedProjectId,
    selectedProjectName,
    setDocumentRouteSlug,
    setSelectedProjectId,
    setSelectedProjectName,
  ]);

  if (window.location.pathname === '/xero-callback') {
    return (
      <Suspense fallback={<ViewLoader />}>
        <XeroCallback />
      </Suspense>
    );
  }
  if (window.location.pathname.startsWith('/wallet/verify/')) {
    const token = decodeURIComponent(window.location.pathname.replace('/wallet/verify/', ''));
    if (token) {
      return (
        <Suspense fallback={<ViewLoader />}>
          <WalletVerificationPage token={token} />
        </Suspense>
      );
    }
  }
  if (window.location.pathname.startsWith('/daywork-sign/')) {
    // Public unauth signing page. URL is /daywork-sign/{token}.
    const token = window.location.pathname
      .replace('/daywork-sign/', '')
      .split(/[/?#]/)[0];
    if (token) {
      return (
        <Suspense fallback={<ViewLoader />}>
          <DayworkSignPage token={decodeURIComponent(token)} />
        </Suspense>
      );
    }
  }
  // Restricted sessions use the normal home URL without loading protected data.
  // Keep the old path as a compatibility entry for existing checkout returns.
  if (workspaceSessionLocked || sessionStorage.getItem(workspaceLockKey) === 'true') return <Suspense fallback={<ViewLoader />}><BillingRecoveryPage sessionLocked onSignOut={() => { void auth.logout(); }} /></Suspense>;
  if (window.location.pathname === '/billing-recovery' ||
      billingRecoveryToken) return <Suspense fallback={<ViewLoader />}><BillingRecoveryPage /></Suspense>;
  if (window.location.pathname.startsWith('/appointment/')) {
    // URL is /appointment/{name-slug}/{token} or /appointment/{token}.
    // The token (last non-empty path segment) is the security key — the name
    // segment is decorative and ignored at lookup.
    const segments = window.location.pathname
      .replace('/appointment/', '')
      .split(/[/?#]/)
      .filter(Boolean);
    const slug = segments.length ? decodeURIComponent(segments[segments.length - 1]) : '';
    if (slug) {
      return (
        <Suspense fallback={<ViewLoader />}>
          <PublicBookingPage slug={slug} />
        </Suspense>
      );
    }
  }
  if (shareToken) {
    return (
      <div className="ios-safe-area">
        <Suspense fallback={<ViewLoader />}>
          <PublicDocumentViewer token={shareToken} />
        </Suspense>
      </div>
    );
  }

  // Dead URL. Sits AFTER the public routes above so share links, booking pages
  // and OAuth callbacks still resolve, and BEFORE the login gate so a bad link
  // says so rather than asking the user to sign in first to be told nothing is
  // there.
  //
  // `entryPathname` is captured on first render, not read live: the app rewrites
  // the URL during boot while it restores the last session's view, so reading it
  // later would show a valid path and make the 404 flash and vanish. Only a
  // popstate — the user actually navigating — refreshes it, which is what lets
  // Back clear this screen.
  if (isUnknownRoute(entryPathname)) {
    return <RouteNotFound pathname={entryPathname} />;
  }

  // Login gate — show LoginPage or RegisterPage when no authenticated user session exists.
  // LoginPage calls onLogin(user, rememberMe) which sets auth.user and persists
  // the user to localStorage with the role fetched from the database.
  if (auth.status === 'unknown') {
    return (
      <Suspense fallback={null}>
        <WebLaunchScreen />
      </Suspense>
    );
  }

  if (!auth.user || auth.status !== 'authenticated') {
    // Checked before signupToken: /join?invite= (joining an existing
    // workspace) and /signup?token= (provisioning a new one) are distinct
    // flows, but both can theoretically be present — invite wins since it's
    // the more specific, admin-initiated action.
    if (inviteToken) {
      return (
        <Suspense fallback={<ViewLoader />}>
          <MemberInvite token={inviteToken} onSignupComplete={(user) => (auth as any).login(user, false)} />
        </Suspense>
      );
    }
    if (signupToken) {
      return (
        <Suspense fallback={<ViewLoader />}>
          <ClientSignup token={signupToken} onSignupComplete={(user) => (auth as any).login(user, false)} />
        </Suspense>
      );
    }
    if (window.location.pathname === '/register' || showRegister) {
      return (
        <Suspense fallback={<ViewLoader />}>
          <RegisterPage onRegister={(auth as any).login} onBackToLogin={() => { setShowRegister(false); window.history.pushState({}, '', '/'); }} />
        </Suspense>
      );
    }
    return (
      <Suspense fallback={null}>
        <LoginPage onLogin={(auth as any).login} />
      </Suspense>
    );
  }

  // Shareable run link for a manual workflow. After the login gate so a signed-out
  // visitor returns to this URL; before the workspace shell because members who
  // can run a workflow cannot open Settings.
  if (entryPathname.startsWith('/workflows/run/')) {
    const workflowId = decodeURIComponent(entryPathname.slice('/workflows/run/'.length).split(/[/?#]/)[0] ?? '');
    if (workflowId) {
      return (
        <Suspense fallback={<ViewLoader />}>
          <WorkflowRunPage id={workflowId} />
        </Suspense>
      );
    }
  }

  // Approval must retain its query through login, without the workspace
  // controller canonicalising legacy Account links and dropping the request.
  if (entryPathname === '/account/gmail-addon' && urlParams.has('gmailConnect')) {
    return <Suspense fallback={<ViewLoader />}>
      <main className="mx-auto max-w-2xl p-6">
        <GmailAddonConnectionPage user={auth.user} compact />
      </main>
    </Suspense>;
  }

  if (entryPathname === '/mcp/authorize' || entryPathname === '/mcp/connections') {
    return <Suspense fallback={<ViewLoader />}>
      <McpConnectionPage email={auth.user.email ?? ''} manage={entryPathname === '/mcp/connections'} />
    </Suspense>;
  }

  return (
    <Suspense fallback={null}>
      <AppShellProviders>
        <AuthenticatedController
          auth={auth}
          currentView={currentView!}
          pageTitle={pageTitle}
          projectActiveTab={projectActiveTab}
          projectPermissions={projectPermissions}
          projectTabProps={projectTabProps}
          workspacesActiveTab={workspacesActiveTab}
          workspacesStats={workspacesStats}
          emailActiveTab={emailActiveTab}
          setEmailActiveTab={setEmailActiveTab}
          mainContractorInitialTab={mainContractorInitialTab}
          setMainContractorInitialTab={setMainContractorInitialTab}
          systemSettingsActiveTab={systemSettingsActiveTab}
          preferencesSection={preferencesSection}
          profileActiveTab={profileActiveTab}
          outreachActiveTab={outreachActiveTab}
          setOutreachActiveTab={setOutreachActiveTab}
          selectedProjectId={selectedProjectId}
          selectedProjectName={selectedProjectName}
          selectedClientId={selectedClientId}
          selectedClientName={selectedClientName}
          clientDetailActiveTab={clientDetailActiveTab}
          selectedTaskId={selectedTaskId}
          calendarTasksTab={calendarTasksTabOpen}
          calendarProjectScope={calendarProjectScope}
          setCalendarProjectScope={setCalendarProjectScope}
          documentRouteSlug={documentRouteSlug}
          eventsRouteSlug={eventsRouteSlug}
          taskRouteSlug={taskRouteSlug}
          messagesChatSlug={messagesChatSlug}
          setPendingChatSlug={setPendingChatSlug}
          showContextualSearch={showContextualSearch}
          notificationOpen={notificationOpen}
          messagePanelOpen={messagePanelOpen}
          logoFullLight={logoFullLight}
          logoFullDark={logoFullDark}
          setNativeShellHidesWebMainHeader={setNativeShellHidesWebMainHeader}
          setProjectTabProps={setProjectTabProps}
          setWorkspacesStats={setWorkspacesStats}
          setSelectedProjectId={setSelectedProjectId}
          setSelectedProjectName={setSelectedProjectName}
          setSelectedClientId={setSelectedClientId}
          setSelectedClientName={setSelectedClientName}
          setClientDetailActiveTab={setClientDetailActiveTab}
          setSelectedTaskId={setSelectedTaskId}
          setCalendarTasksTab={setCalendarTasksTab}
          setDocumentRouteSlug={setDocumentRouteSlug}
          setEventsRouteSlug={setEventsRouteSlug}
          setTaskRouteSlug={setTaskRouteSlug}
          setMessagesChatSlug={setMessagesChatSlug}
          setProjectActiveTab={setProjectActiveTab}
          setSystemSettingsActiveTab={setSystemSettingsActiveTab}
          setPreferencesSection={setPreferencesSection}
          setProfileActiveTab={setProfileActiveTab}
          setWorkspacesActiveTab={setWorkspacesActiveTab}
          setShareToken={setShareToken}
          navigateToView={navigateToView}
          setCurrentViewImmediate={setCurrentView}
          getCompany={getCompany}
          isPopStateRef={isPopState}
          handleOpenChatThread={handleOpenChatThread}
          handleShowCalendarWithTask={handleShowCalendarWithTask}
          handleSelectProject={handleSelectProject}
          toggleMessagesPanel={toggleMessagesPanel}
          toggleNotificationsPanel={toggleNotificationsPanel}
          setNotificationOpen={setNotificationOpen}
          setMessagePanelOpen={setMessagePanelOpen}
          setPanelChatId={setPanelChatId}
          setPanelThreadMessageId={setPanelThreadMessageId}
        />
        <AuthenticatedWorkspace
          auth={auth}
          currentView={currentView!}
          isNavigating={isNavigating}
          shellConfig={shellConfig}
          shouldPassWebHeader={shouldPassWebHeader}
          nativeShellHidesWebMainHeader={nativeShellHidesWebMainHeader}
          pageTitle={pageTitle}
          navigateToView={handleWorkspaceNavigate}
          handleLogout={handleLogout}
          toggleNotificationsPanel={toggleNotificationsPanel}
          toggleMessagesPanel={toggleMessagesPanel}
          notificationOpen={notificationOpen}
          messagePanelOpen={messagePanelOpen}
          taskQueueOpen={calendarTasksTabOpen}
          toggleTaskQueuePanel={toggleTaskQueuePanel}
          bookingPanelOpen={bookingPanelOpen}
          toggleBookingPanel={toggleBookingPanel}
          panelChatId={panelChatId}
          panelThreadMessageId={panelThreadMessageId}
          closeRightPanels={closeRightPanels}
          showContextualSearch={showContextualSearch}
          activateContextualSearch={activateContextualSearch}
          projectActiveTab={projectActiveTab}
          setProjectActiveTab={setProjectActiveTab}
          projectTabProps={projectTabProps}
          projectPermissions={projectPermissions}
          setProjectPermissions={setProjectPermissions}
          projectListProps={projectListProps}
          workspacesActiveTab={workspacesActiveTab}
          setWorkspacesActiveTab={setWorkspacesActiveTab}
          workspacesStats={workspacesStats}
          selectedProjectId={selectedProjectId}
          selectedProjectName={selectedProjectName}
          handleSelectProject={handleSelectProject}
          handleSelectProjectTab={handleSelectProjectTab}
          projectFilterStage={projectFilterStage}
          documentRouteSlug={documentRouteSlug}
          setDocumentRouteSlug={setDocumentRouteSlug}
          eventsRouteSlug={eventsRouteSlug}
          setEventsRouteSlug={setEventsRouteSlug}
          handleCloseProjectDetail={handleCloseProjectDetail}
          selectedTaskId={selectedTaskId}
          setSelectedTaskId={setSelectedTaskId}
          setTaskRouteSlug={setTaskRouteSlug}
          initialTaskId={selectedTaskId ?? undefined}
          calendarProjectScope={calendarProjectScope}
          handleShowCalendarWithTask={handleShowCalendarWithTask}
          handleShowInterimValuations={handleShowInterimValuations}
          handleShowMessages={handleShowMessages}
          handleShowNotifications={handleShowNotifications}
          handleShowDashboard={handleShowDashboard}
          handleShowUserManagement={handleShowUserManagement}
          setCurrentView={setCurrentView}
          handleShowAdminPanel={handleShowAdminPanel}
          mainContractorInitialTab={mainContractorInitialTab}
          setMainContractorInitialTab={setMainContractorInitialTab}
          selectedUserId={selectedUserId}
          setSelectedUserId={setSelectedUserId}
          newlyCreatedUser={newlyCreatedUser}
          setNewlyCreatedUser={setNewlyCreatedUser}
          userManagementActiveTab={userManagementActiveTab}
          setUserManagementActiveTab={setUserManagementActiveTab}
          handleShowAddUserForm={handleShowAddUserForm}
          selectedClientId={selectedClientId}
          selectedClientName={selectedClientName}
          handleSelectClient={handleSelectClient}
          handleCloseClientDetail={handleCloseClientDetail}
          clientDetailActiveTab={clientDetailActiveTab}
          setClientDetailActiveTab={setClientDetailActiveTab}
          systemSettingsActiveTab={systemSettingsActiveTab}
          setSystemSettingsActiveTab={setSystemSettingsActiveTab}
          preferencesSection={preferencesSection}
          setPreferencesSection={setPreferencesSection}
          profileActiveTab={profileActiveTab}
          outreachActiveTab={outreachActiveTab}
          setProfileActiveTab={setProfileActiveTab}
          emailActiveTab={emailActiveTab}
          setEmailActiveTab={setEmailActiveTab}
          handleShowSystemSettings={handleShowSystemSettings}
          handleShowFinanceBilling={handleShowFinanceBilling}
          clientRequestActiveTab={clientRequestActiveTab}
          setClientRequestActiveTab={setClientRequestActiveTab}
          clientRequestCompletedTabs={clientRequestCompletedTabs}
          setClientRequestCompletedTabs={setClientRequestCompletedTabs}
          addProjectActiveTab={addProjectActiveTab}
          setAddProjectActiveTab={setAddProjectActiveTab}
          addProjectCompletedTabs={addProjectCompletedTabs}
          setAddProjectCompletedTabs={setAddProjectCompletedTabs}
          handleShowProjects={handleShowProjects}
          handleShowMainContractors={handleShowEmployers}
          interimInitialTab={interimInitialTab}
          pendingChatId={pendingChatId}
          setPendingChatId={setPendingChatId}
          pendingChatSlug={pendingChatSlug}
          setPendingChatSlug={setPendingChatSlug}
          getCompany={getCompany}
          onNotesCountChange={handleNotesCountChange}
        />
      </AppShellProviders>
    </Suspense>
  );
}
AppContent.displayName = 'AppContent';

export default function App() {
  const nativePanel =
    typeof window !== 'undefined'
      ? (
          new URLSearchParams(window.location.search).get('nativePanel')
          ?? new URLSearchParams(window.location.search).get('nativeInspector')
        )
      : null;
  const nativePanelChatId =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('chatId')
      : null;
  const isNativeSidePanelMode =
    nativePanel === 'messages' || nativePanel === 'notifications';
  // `?embed=add-project` renders ONLY the New Project form, for hosting inside
  // a native iOS .sheet (a webview loads this URL). Same providers, no shell.
  const embedMode =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('embed')
      : null;
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const callbackParams = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search)
    : new URLSearchParams();

  if (
    pathname === '/auth/google-calendar/callback' ||
    (pathname === '/' && (callbackParams.has('code') || callbackParams.has('state') || callbackParams.has('error')))
  ) {
    return (
      <GoogleWorkspaceCallbackBridge
        code={callbackParams.get('code')}
        state={callbackParams.get('state')}
        error={callbackParams.get('error')}
      />
    );
  }

  return (
    <AppProviders>
      {pathname === '/start' ? (
        <Suspense fallback={<ViewLoader />}><StartSoftwarePage /></Suspense>
      ) : embedMode === 'add-project' ? (
        <NativeAddProjectApp />
      ) : isNativeSidePanelMode ? (
        <NativeSidePanelApp
          initialPanel={nativePanel as 'messages' | 'notifications'}
          initialChatId={nativePanelChatId}
        />
      ) : (
        <AppContent />
      )}
    </AppProviders>
  );
}
